import React, { ChangeEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  TextField,
  MenuItem,
  Select
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { useGetUserDetailQuery, useUpdateUserMutation, useUploadMediaMutation } from "../../service/features/api";
import { useEffect } from "react";
import { GENDER } from "../../constants/enum";
import { toast } from "react-toastify";

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAddUser = !id;

  const { data: userDetail, isLoading } = useGetUserDetailQuery(id, { skip: !id });
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  const userData = userDetail?.data;

  const [image, setImage] = useState<any>("");
  const [displayImage, setDisplayImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [gender, setGender] = React.useState<string | number>("default");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    countryCode: "",
    country: "",
    state: "",
    city: ""
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        fullName: userData.fullName || "",
        email: userData.email || "",
        phone: userData.phone || "",
        countryCode: userData.countryCode || "",
        country: userData.address?.country || "",
        state: userData.address?.state || "",
        city: userData.address?.city || ""
      });
      setGender(userData.gender || "default");
      setImage(userData.image || "");
      setDisplayImage(userData.image || "");
    }
  }, [userData]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "fullName" && value.length > 50) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.fullName.trim()) return toast.error("Full Name is required");
    if (!formData.email.trim()) return toast.error("Email is required");
    if (!formData.phone.trim()) return toast.error("Phone number is required");

    let imageUrl = displayImage;

    if (image instanceof File) {
      const formDataImage = new FormData();
      formDataImage.append("file", image);
      try {
        const res = await uploadMedia(formDataImage).unwrap();
        imageUrl = res?.data?.data || res?.data || res?.url;
        toast.success("Image uploaded successfully");
      } catch (err) {
        return toast.error("Failed to upload image");
      }
    }

    try {
      const payload = {
        fullName: formData.fullName.trim(),
        address: {
          city: formData.city,
          state: formData.state,
          country: formData.country
        },
        image: imageUrl,
        isBlocked: true, // As per request
        role: 3
      };

      await updateUser({ id, ...payload }).unwrap();
      toast.success("User updated successfully");
      navigate("/manage-users");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update user");
    }
  };

  return (
    <>
      {/* PAGE TITLE */}
      <div className="main_title">
        <h1>{isAddUser ? "Add" : "Edit"} Customer</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-users")}>
            Customer Management
          </button>{" "}
          - {isAddUser ? "Add" : "Edit"} Customer
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">

            {/* UPLOAD IMAGE */}
            <div className="control_group w_100">
              <label>Upload Image</label>

              {displayImage ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={displayImage} alt="User" />
                    </figure>
                    <CloseIcon
                      className="remove_img"
                      onClick={() => {
                        setImage("");
                        setDisplayImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="upload-user-image">
                  <Input
                    sx={{ display: "none" }}
                    id="upload-user-image"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const files = event.target.files;
                      if (files && files[0].type.includes("image")) {
                        setFileName(files[0].name);
                        setImage(files[0]);
                        setDisplayImage(URL.createObjectURL(files[0]));
                      }
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="User"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="Edit"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* FULL NAME */}
            <div className="control_group w_33">
              <label>Full Name</label>
              <TextField
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter Full Name"
                hiddenLabel
                fullWidth
              />
            </div>

            {/* EMAIL */}
            <div className="control_group w_33">
              <label>Email</label>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder="Enter Email"
                hiddenLabel
                fullWidth
              />
            </div>

            {/* PHONE */}
            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                country={"in"}
                value={formData.countryCode + formData.phone}
                onChange={(phone, country: any) => {
                  setFormData(prev => ({
                    ...prev,
                    phone: phone.slice(country.dialCode.length),
                    countryCode: `+${country.dialCode}`
                  }));
                }}
                placeholder="Enter Phone Number"
                inputStyle={{ width: "100%" }}
                enableSearch={false}
              />
            </div>

            {/* GENDER */}
            <div className="control_group w_33">
              <label>Gender</label>
              <Select
                fullWidth
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <MenuItem value="default" disabled>
                  Select Gender
                </MenuItem>
                <MenuItem value={GENDER.MALE}>Male</MenuItem>
                <MenuItem value={GENDER.FEMALE}>Female</MenuItem>
                <MenuItem value={GENDER.OTHER}>Other</MenuItem>
              </Select>
            </div>

            {/* COUNTRY */}
            <div className="control_group w_33">
              <label>Country</label>
              <TextField
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Enter Country"
                hiddenLabel
                fullWidth
              />
            </div>

            {/* STATE */}
            <div className="control_group w_33">
              <label>State</label>
              <TextField
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Enter State"
                hiddenLabel
                fullWidth
              />
            </div>

            {/* CITY */}
            <div className="control_group w_33">
              <label>City</label>
              <TextField
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter City"
                hiddenLabel
                fullWidth
              />
            </div>
          </div>

          {/* SAVE BUTTON */}
          <div className="form_btn">
            <Button className="btn btn_primary" onClick={handleSave} disabled={isUpdating || isUploading}>
              {isUpdating || isUploading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUser;
